import { useEffect, useState, type ComponentProps, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, SlidersHorizontal, Square } from 'lucide-react';
import { CenteredSelect } from './CenteredSelect';
import { WrittenCallBadge } from './WrittenCallBadge';
import { useSlot } from '../../components/sidebarSlot';
import type { RunProgress } from './openaiBlenderApi';
// The clock counts from the run's own press, kept with the run — see useSince.
import { useSince } from './waitClock';
import { useLanguage } from '../../contexts/LanguageContext';

// WHAT IS ABOUT TO BE SPENT, AND THE BUTTON THAT SPENDS IT: how hard, how many passes,
// go, stop, and the badge that says what would be sent.
//
// ONE COMPONENT BECAUSE IT IS ONE CONTROL. Phase 5 asks this in two places — of a part, and
// of the whole product — and they were two copies of the same four controls in the same
// order. Copies drift silently: the pass dropdown was added to both by hand on the same
// afternoon, and the day one of them gains a third dropdown the other will not.
//
// What differs between the two callers is what the button DOES and what the badge is about,
// which are the two things handed in.
//
// A BADGE IN THE PHASE'S ROW, NOT A CARD ON THE PAGE. It was a card under the viewer — pictures,
// dropdowns, a big button — for each of the product, a set and a part. It is the phase's
// "3D model generation" badge now, before Find holes (GENERATE_SLOT_ID, in ModelsPhase), drawn
// there by whichever of the three is open: its Run is the press with what is chosen, and its
// first row opens the pictures and the settings in the window every call's prompt opens in.
// A portal rather than state lifted to the phase: each generator keeps what it knows, and only
// where its badge is drawn moves.
export const GENERATE_SLOT_ID = 'models-generate-slot';
// WHICH SUBPHASE'S GENERATOR, said on the badge after its name — the same badge in one place
// makes two different things.
export type GenerateScope = 'product' | 'hardware';

export function MeshGenerateControls({
  effort, effortChoices, onEffort, passes, passChoices, onPasses,
  busy, since, progress, canRun, onRun, onStop, call, options, pictures, picksCrops = false, scope, heading,
}: {
  // WHAT IS BEING GENERATED, over the settings — what the call is about, where the caller has no
  // better name for it (a set's, with its pieces made under Tools in brackets).
  heading?: string;
  scope: GenerateScope;
  // HOW HARD THE WRITTEN MODEL THINKS, and what the chosen model takes — see effortChoices.
  // Between the model and the passes: it is a property of the model's calls, and the passes are
  // how many of those calls there are.
  effort: string;
  effortChoices: string[];
  onEffort: (effort: string) => void;
  // HOW MANY TIMES THE WRITTEN ENGINE MODELS IT, and what it may be: a pass is a call shown
  // renders of what the last one built.
  passes: number;
  passChoices: number[];
  onPasses: (passes: number) => void;
  // A RUN IS IN FLIGHT. Every control here goes quiet, the button counts the seconds, and
  // the stop appears beside it.
  busy: boolean;
  // WHEN THE RUN WAS PRESSED, as the run's own record keeps it (see generatorMemory) — so the
  // clock survives this panel being thrown away and drawn again mid-run.
  since?: number;
  // HOW FAR THE RUN HAS GOT — see RunProgress. Absent until the first poll has answered.
  progress?: RunProgress;
  // Whether the press would do anything: a picture chosen, a key configured, the page not
  // locked. The caller's question — each has its own list — and this only draws the answer.
  canRun: boolean;
  onRun: () => void;
  // HOW IT IS CALLED OFF.
  onStop?: () => void;
  // WHAT WOULD BE SENT: the written engine's badge, as the next press would ask.
  call: ComponentProps<typeof WrittenCallBadge>;
  // THE PICTURES TO CHOOSE FROM — in the prompt row, over the words they change (see PromptPanel);
  // `picksCrops`: they include the close-ups the call sends. (A failed run is not said here any
  // more: it has its tile in the strip and the viewer's place — see FailedRunPanel.)
  pictures: ReactNode;
  picksCrops?: boolean;
  // What else the press can be told, above the button — a product's "include parts crops".
  options?: ReactNode;
}) {
  const { t } = useLanguage();
  // THE CLOCK IS IN THE LABEL, which is what the bench pages settled on for the same wait: a
  // run takes thirty to sixty seconds and reports nothing in between, so a counting number
  // is the only honest progress there is, and the button is the thing being watched. A
  // spinner alone cannot say whether it has been eight seconds or eighty.
  //
  // COUNTED FROM THE PRESS, not from this component's mount: it used to be held here, and
  // switching tab and back mid-run started it again at nought on a run a minute old.
  const slot = useSlot(GENERATE_SLOT_ID);
  const elapsed = useSince(busy ? since ?? null : null);
  // WHETHER THE LAST RUN WAS STOPPED HERE, so its badge says no "took" (see CallRun.stopped).
  // Forgotten when the next one starts.
  const [stopped, setStopped] = useState(false);
  useEffect(() => { if (busy) setStopped(false); }, [busy]);
  const stop = onStop && (() => { setStopped(true); onStop(); });

  const named = heading ?? call.subject;
  const settings = (
    <div className="flex flex-col items-center gap-2">
      {named && <p className="mb-1 text-center text-sm font-semibold text-slate-800">{named}</p>}
      {/* HOW HARD IT THINKS. */}
      {effortChoices.length > 1 && (
        <CenteredSelect
          value={effort}
          options={effortChoices.map((e) => ({ value: e, label: t(`notice.3d.effort.${e}`) }))}
          onChange={onEffort}
          ariaLabel={t('notice.3d.effortLabel')}
          disabled={busy}
        />
      )}
      {/* AND HOW MANY PASSES, under it — the order the two decisions are taken in, and both
          of them about the same press. */}
      {passChoices.length > 1 && (
        <CenteredSelect
          value={String(passes)}
          options={passChoices.map((n) => ({
            value: String(n),
            label: t(n === 1 ? 'notice.3d.passOne' : 'notice.3d.passMany')
              .replace('{n}', String(n)),
          }))}
          onChange={(v) => onPasses(Number(v))}
          ariaLabel={t('notice.3d.passCount')}
          disabled={busy}
        />
      )}
      {options}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRun}
          disabled={busy || !canRun}
          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 disabled:opacity-40"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {busy ? `${t('notice.3d.generating')} ${elapsed}s` : t('notice.3d.generate')}
        </button>
        {/* STOP, while there is something to stop. Its own button beside the running one
            rather than the running one turning into it: a spinner somebody is watching is a
            poor place to put a control they did not mean to press. */}
        {busy && stop && (
          <button
            type="button"
            onClick={stop}
            // THE STOP'S ONE LOOK, the badge menu's row (see CallMenu): red words and an open square.
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-rose-600 transition-colors hover:bg-slate-100"
          >
            <Square className="h-3.5 w-3.5" />
            {t('notice.cat.stop')}
          </button>
        )}
      </div>
      {/* WHERE THE RUN IS, under the clock that says how long: which pass, and what it is
          doing. Only what the server knows — nothing here guesses how much thinking is left,
          because nothing reports it. */}
      {busy && progress && (
        <p className="text-[11px] font-medium text-slate-500">
          {progress.passes > 1 && `${t('notice.3d.progressPass')
            .replace('{n}', String(progress.pass))
            .replace('{of}', String(progress.passes))} · `}
          {t(`notice.3d.stage.${progress.stage}`)}
        </p>
      )}
    </div>
  );

  if (!slot) return null;
  return createPortal(
    <WrittenCallBadge
      {...call}
      picker={{ node: pictures, crops: picksCrops }}
      nextPress
      title={`${t('notice.3d.boxGenerate')} (${t(`notice.3d.genScope.${scope}`)})`}
      press={{
        label: t('notice.3d.generate'),
        onRun,
        busy,
        disabled: !canRun,
        elapsed: busy ? elapsed : undefined,
        stopped: !busy && stopped,
        onStop: stop,
        // NOT A PRESS FROM THE MENU: it opens the pictures and settings, whose button is the
        // press — what it spends is what is chosen there.
        opens: 'settings',
      }}
      lead={[{
        key: 'settings',
        icon: <SlidersHorizontal className="h-3.5 w-3.5" />,
        label: t('notice.3d.settingsRow'),
        panel: settings,
      }]}
    />,
    slot,
  );
}
